/**
 * Maps the structure `generateCRFVariables` chose onto the `saveCrfForm`
 * payload.
 *
 * WHY IT IS A SEPARATE PURE FUNCTION. `web` has no test runner and must not
 * gain one (most of its node_modules is git-tracked), so the frontend's
 * testable logic lives in files like this one and is exercised from
 * `functions` via `npm run test:web`. Inlining this in CRFBuilder.tsx would
 * put it beyond reach of any test.
 *
 * THE CONTRACT THAT MATTERS: a form with no structure contributes NOTHING,
 * so every payload the CRF Builder built before this existed is unchanged.
 * That is what keeps the manual-edit, REDCap-import and packet-extract paths
 * behaving exactly as they did.
 */

/** The `structure` object returned by generateCRFVariables. Untrusted. */
export interface ClientStructureChoice {
  layout?: string;
  orientation?: string;
  instructions?: string;
  reason?: string;
  source?: 'model' | 'default';
  fallbackReason?: string;
  codeTables?: Array<{ id: string; title: string; codes: Array<{ value: number | string; label: string }> }>;
  /**
   * The sections the structure pass chose, grids and tables included. Read
   * once, by sectionsFromStructure (crfLayoutSections.ts), to build the form's
   * own sections; never persisted from here (2026-09-18).
   */
  sections?: unknown;
}

export interface StructurePayloadFields {
  layout?: string;
  orientation?: string;
  instructions?: string;
  structureRationale?: string;
  structureSource?: 'model' | 'default';
  structureFallbackReason?: string;
  codeTables?: ClientStructureChoice['codeTables'];
}

const LAYOUTS = new Set(['question-list', 'log-table']);
const ORIENTATIONS = new Set(['portrait', 'landscape']);

export function structurePayloadFields(
  structure: ClientStructureChoice | null | undefined,
): StructurePayloadFields {
  if (!structure || typeof structure !== 'object') return {};
  // No usable layout means no structure to persist. Sending a partial one
  // would write a choice nobody made into the provenance record.
  if (typeof structure.layout !== 'string' || !LAYOUTS.has(structure.layout)) {
    // The form-level instruction line is content, not a choice: the
    // content pass writes one onto a form that chose no layout, and it
    // must not be lost for want of a layout beside it. It creates no
    // provenance record on the server (that is gated on layout/source).
    return structure.instructions ? { instructions: structure.instructions } : {};
  }

  const out: StructurePayloadFields = { layout: structure.layout };

  if (typeof structure.orientation === 'string' && ORIENTATIONS.has(structure.orientation)) {
    out.orientation = structure.orientation;
  }
  if (structure.instructions) out.instructions = structure.instructions;
  if (Array.isArray(structure.codeTables) && structure.codeTables.length > 0) {
    out.codeTables = structure.codeTables;
  }

  // A default and a model choice are recorded differently on purpose. A
  // default never carries a rationale — the point of the field is that a
  // form which got no decision cannot be mistaken for one that did, and a
  // reason attached to a default is exactly that mistake. Guarded here as
  // well as on the server because a malformed reply could carry both.
  if (structure.source === 'default') {
    out.structureSource = 'default';
    if (structure.fallbackReason) out.structureFallbackReason = structure.fallbackReason;
  } else {
    if (structure.source) out.structureSource = structure.source;
    if (structure.reason) out.structureRationale = structure.reason;
  }

  return out;
}
